// src/pages/MyWallet.jsx
//
// Student-facing page: everything the student can spend, laid out in
// two clearly separate sections — a general store credit balance
// (usable towards anything in the cart, users.credit_balance) and a
// per-program GEFEN wallet breakdown (each balance usable only
// towards that specific program's own lessons, topped up by
// redeeming coupons on /RedeemCoupon). Read-only: this page never
// changes a balance, it just shows where things stand.
//
// Structure mirrors ContactFormPreview.jsx exactly: a big header is
// shown ONLY on mobile as a sticky bar, while everything (including
// the header) lives inside one white card on every size, with a
// gradient top strip and a compact desktop-only CardHeader row
// (icon + title, border-b) instead of repeating the big header.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useCouponRedemption } from '../contexts/CouponRedemptionContext';
import WalletHeader from '../components/ui/WalletHeader';
import SpinnerOverlay from '../components/ui/SpinnerOverlay';

const formatMoney = (v) => `₪${Number(v || 0).toLocaleString('he-IL', { maximumFractionDigits: 2 })}`;

// /Search reads ?programId= on mount and jumps straight to that GEFEN
// program's own open courses (see SearchPage.jsx + SearchContext's
// searchByProgram) — programId here is programs.id, NOT
// w.schoolProgramId (school_programs.id), so it only works once the
// wallets() API also returns that field. See note below.
const coursesLinkFor = (w) => `/Search?programId=${w.programId ?? w.schoolProgramId}`;

// ─── Card Header (desktop / tablet) ──────────────────────────────────────────

const CardHeader = ({ hasChips, programsCount, totalCredit }) => (
  <div className="hidden sm:flex items-center justify-between gap-3 mb-5 pb-4 border-b-2 border-gray-400">
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white flex items-center justify-center shadow-sm">
        <svg
          viewBox="0 0 24 24"
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
          <path d="M2.5 9.5h19" />
          <circle cx="16.5" cy="14.5" r="1.4" />
        </svg>
      </div>

      <div className="min-w-0">
        <h2 className="text-base font-bold text-gray-800 leading-tight">הארנק שלי</h2>
        <p className="text-xs text-gray-400 mt-0.5">קרידיט כללי לשימוש חופשי, וקרידיט גפ״ן לפי תוכנית</p>
      </div>
    </div>

    {hasChips && (
      <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-1.5">
        <span className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2.5 py-1 text-xs whitespace-nowrap">
          {programsCount} תוכניות
        </span>
        <span className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 border border-purple-100 px-2.5 py-1 text-xs whitespace-nowrap">
          סה״כ קרידיט: {formatMoney(totalCredit)}
        </span>
      </div>
    )}
  </div>
);

const MyWallet = () => {
  const { getWallets } = useCouponRedemption();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [wallets, setWallets] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getWallets();
      if (!data?.ok) {
        throw new Error(data?.error || 'שגיאה בטעינת הארנק');
      }
      setCreditBalance(Number(data.creditBalance || 0));
      setWallets(Array.isArray(data.wallets) ? data.wallets : []);
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הארנק');
    } finally {
      setLoading(false);
    }
  }, [getWallets]);

  useEffect(() => {
    load();
  }, [load]);

  const walletsTotal = wallets.reduce((sum, w) => sum + Number(w.balance || 0), 0);
  const hasChips = !loading && !error;

  return (
    <div dir="rtl" className="min-h-dvh bg-gray-100">
      {loading && <SpinnerOverlay variant="glow" text="טוען את הארנק..." dark={false} blur={false} />}

      <div className="sm:hidden sticky top-0 z-10">
        <WalletHeader
          programsCount={hasChips ? wallets.length : undefined}
          totalCredit={hasChips ? creditBalance + walletsTotal : undefined}
        />
      </div>

      <main className="mx-auto max-w-2xl px-2 pt-2 pb-8 lg:py-10">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
          <div className="h-1 bg-gradient-to-l from-indigo-500 via-purple-500 to-blue-500" />

          <div className="p-4 sm:p-6">
            <CardHeader
              hasChips={hasChips}
              programsCount={wallets.length}
              totalCredit={creditBalance + walletsTotal}
            />

            {error ? (
              <div className="text-center py-8 space-y-3">
                <p className="text-sm text-red-600">{error}</p>
                <button
                  type="button"
                  onClick={load}
                  className="inline-flex items-center rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold px-5 py-2.5 shadow-sm hover:opacity-90 active:scale-[0.98] transition-all"
                >
                  ניסיון נוסף
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Section 1: general, program-agnostic store credit. */}
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500">קרידיט כללי</h3>
                  <div className="rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-purple-50 p-5 text-center">
                    <p className="text-xs text-indigo-700">יתרת קרידיט כללית</p>
                    <p className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-l from-indigo-600 to-purple-600 bg-clip-text text-transparent mt-1">
                      {formatMoney(creditBalance)}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">ניתן לשימוש בכל רכישה בעגלה</p>
                  </div>
                </section>

                {/* Section 2: per-program GEFEN wallets — a completely
                    separate pool of money, each usable only within its
                    own program's lessons. */}
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold text-gray-500">קרידיט גפ״ן לפי תוכנית</h3>

                  {wallets.length === 0 ? (
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-6 text-center space-y-2">
                      <div className="text-2xl">🎟️</div>
                      <p className="text-sm text-gray-500">עדיין אין לך קרידיט גפ״ן בשום תוכנית</p>
                      <p className="text-[11px] text-gray-400">סרוק קופון גפ״ן כדי להוסיף קרידיט לתוכנית</p>
                    </div>
                  ) : (
                    <>
                      {/* Mobile: one card per program. A 3-column table
                          squeezed the program name, supplier and balance
                          into too little width to read on a phone — the
                          supplier name in particular kept getting
                          dropped/truncated to nothing. Each field now
                          gets its own row, so nothing is hidden. */}
                      <div className="sm:hidden space-y-2">
                        {wallets.map((w) => (
                          <div
                            key={w.schoolProgramId}
                            className="rounded-xl border border-gray-100 p-3 space-y-2.5"
                          >
                            <div className="flex items-center gap-2.5">
                              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm shrink-0 shadow-sm">
                                🎫
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-800 truncate text-sm">{w.programName}</p>
                                <p className="text-[11px] text-gray-400 truncate">{w.supplierName || '—'}</p>
                              </div>
                              <span className="font-bold bg-gradient-to-l from-indigo-600 to-purple-600 bg-clip-text text-transparent shrink-0 text-sm">
                                {formatMoney(w.balance)}
                              </span>
                            </div>
                            <Link
                              to={coursesLinkFor(w)}
                              className="flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-semibold py-2 hover:bg-indigo-100 transition-colors"
                            >
                              הרשמה לקורסים בתוכנית זו
                            </Link>
                          </div>
                        ))}
                        <div className="rounded-xl bg-indigo-50/60 border border-indigo-100 px-3 py-2 flex items-center justify-between">
                          <span className="font-semibold text-gray-600 text-xs">סה״כ</span>
                          <span className="font-extrabold text-indigo-700 text-sm">{formatMoney(walletsTotal)}</span>
                        </div>
                      </div>

                      {/* Desktop / tablet: table, all columns side by side. */}
                      <div className="hidden sm:block rounded-xl border border-gray-100 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gradient-to-l from-indigo-50 to-purple-50 border-b border-indigo-100">
                              <th className="text-start font-semibold text-indigo-700 px-4 py-2.5 text-xs">תוכנית</th>
                              <th className="text-start font-semibold text-indigo-700 px-4 py-2.5 text-xs">ספק</th>
                              <th className="text-end font-semibold text-indigo-700 px-4 py-2.5 text-xs">יתרה</th>
                              <th className="px-4 py-2.5 text-xs"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {wallets.map((w, i) => (
                              <tr
                                key={w.schoolProgramId}
                                className={`border-b border-gray-50 last:border-0 ${i % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xs shrink-0 shadow-sm">
                                      🎫
                                    </div>
                                    <p className="font-medium text-gray-800 truncate min-w-0">{w.programName}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-500 text-xs">{w.supplierName || '—'}</td>
                                <td className="px-4 py-3 text-end">
                                  <span className="font-bold bg-gradient-to-l from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                    {formatMoney(w.balance)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-end">
                                  <Link
                                    to={coursesLinkFor(w)}
                                    className="inline-flex items-center rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs font-semibold px-3 py-1.5 hover:bg-indigo-100 transition-colors whitespace-nowrap"
                                  >
                                    הרשמה לקורסים
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-indigo-50/60 border-t border-indigo-100">
                              <td className="px-4 py-2.5 font-semibold text-gray-600 text-xs" colSpan={2}>
                                סה״כ
                              </td>
                              <td className="px-4 py-2.5 text-end font-extrabold text-indigo-700" colSpan={2}>
                                {formatMoney(walletsTotal)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </>
                  )}
                </section>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MyWallet;